/**
 * Recording-pipeline rules kept outside AudioRecorder so chunk, silence, and
 * audio-test decisions can fail in unit tests instead of during a visit.
 */

export const EMPTY_FIRST_CHUNK_MAX_BYTES = 110;
export const SKIP_INVALID_CHUNK_MAX_BYTES = 150;
export const SUSPICIOUS_CHUNK_MAX_BYTES = 1000;
export const CONSECUTIVE_INVALID_CHUNKS_TO_STOP = 2;
// Levels come from 8-bit samples, so the hiss of a muted or covered mic already
// reads ~0.025. Speech peaks well above 0.05, so anything below counts as silence.
export const VOLUME_SILENCE_THRESHOLD = 0.05;
// Long enough to ride out natural pauses in a visit at that threshold.
export const SILENCE_WARNING_MS = 10000;
// Patients may not speak right away, so give the shared tab time before warning.
export const NO_PATIENT_AUDIO_WARNING_SECONDS = 30;
export const VOLUME_VISUAL_GAIN = 3.2;
export const WEB_AUDIO_SILENCE_BYTE = 128;
export const AUDIO_TEST_DURATION_MS = 5000;
export const MERGE_CHUNK_LOCK = 240;
export const CHUNK_INTERVAL_SECONDS = 30;
// The first file is produced by stop() at the chunk interval. This grace period
// starts only once that first stop() has actually been issued, so a throttled
// tab that cuts late is never mistaken for a session with no audio; only a
// stop() that delivers no accepted chunk within the grace period is.
export const FIRST_CHUNK_GRACE_SECONDS = 10;

export type RecordingStatus = "inactive" | "recording" | "paused";

export type ChunkDisposition =
  | { action: "empty-first-chunk-error" }
  | { action: "skip-invalid" }
  | { action: "skip-empty" }
  | { action: "accept" };

export function classifyAudioChunk({
  chunkNumber,
  sizeBytes,
}: {
  chunkNumber: number;
  sizeBytes: number;
}): ChunkDisposition {
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return chunkNumber === 0 ? { action: "empty-first-chunk-error" } : { action: "skip-invalid" };
  }

  // An initial 0-byte flush is not a recording error. Persistent empty output
  // is caught by the first-chunk deadline, which still expects an accepted chunk.
  if (sizeBytes === 0) {
    return { action: "skip-empty" };
  }

  if (chunkNumber === 0 && sizeBytes <= EMPTY_FIRST_CHUNK_MAX_BYTES) {
    return { action: "empty-first-chunk-error" };
  }

  if (sizeBytes < SKIP_INVALID_CHUNK_MAX_BYTES) {
    return { action: "skip-invalid" };
  }

  return { action: "accept" };
}

export function shouldStopForInvalidChunks(consecutiveInvalidChunks: number): boolean {
  return consecutiveInvalidChunks >= CONSECUTIVE_INVALID_CHUNKS_TO_STOP;
}

export function hasAudibleLevel(
  level: number,
  threshold: number = VOLUME_SILENCE_THRESHOLD
): boolean {
  return Number.isFinite(level) && level > threshold;
}

export function shouldShowSilenceWarning({
  combinedLevel,
  telehealthMicLevel,
  isTelehealth,
  silenceMs,
  recordingStatus,
}: {
  combinedLevel: number;
  telehealthMicLevel: number;
  isTelehealth: boolean;
  silenceMs: number;
  recordingStatus: RecordingStatus;
}): boolean {
  const hasSignal =
    hasAudibleLevel(combinedLevel) || (isTelehealth && hasAudibleLevel(telehealthMicLevel));
  return (
    !hasSignal &&
    recordingStatus === "recording" &&
    Number.isFinite(silenceMs) &&
    silenceMs > SILENCE_WARNING_MS
  );
}

export function shouldShowNoPatientAudioWarning({
  isTelehealth,
  hasDetectedSharedAudio,
  recordingStatus,
  elapsedSeconds,
}: {
  isTelehealth: boolean;
  hasDetectedSharedAudio: boolean;
  recordingStatus: RecordingStatus;
  elapsedSeconds: number;
}): boolean {
  return (
    isTelehealth &&
    !hasDetectedSharedAudio &&
    recordingStatus === "recording" &&
    Number.isFinite(elapsedSeconds) &&
    elapsedSeconds >= NO_PATIENT_AUDIO_WARNING_SECONDS
  );
}

export function canStartAudioTest({
  isAudioTestStarting,
  recordingStatus,
  hasRecorded,
}: {
  isAudioTestStarting: boolean;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
}): boolean {
  return !isAudioTestStarting && recordingStatus === "inactive" && !hasRecorded;
}

export function rmsNormalizedFromTimeDomain(
  samples: ArrayLike<number>,
  gain: number = VOLUME_VISUAL_GAIN
): number {
  if (!samples.length) {
    return 0;
  }

  let sumSquares = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const normalized = (samples[index] - WEB_AUDIO_SILENCE_BYTE) / WEB_AUDIO_SILENCE_BYTE;
    sumSquares += normalized * normalized;
  }

  return Math.min(Math.sqrt(sumSquares / samples.length) * gain, 1);
}

export function shouldFailFirstChunkDeadline({
  hasAcceptedChunk,
  secondsSinceFirstCut,
  recordingStatus,
  graceSeconds = FIRST_CHUNK_GRACE_SECONDS,
}: {
  hasAcceptedChunk: boolean;
  secondsSinceFirstCut: number;
  recordingStatus: RecordingStatus;
  graceSeconds?: number;
}): boolean {
  return (
    recordingStatus === "recording" &&
    !hasAcceptedChunk &&
    Number.isFinite(secondsSinceFirstCut) &&
    secondsSinceFirstCut >= graceSeconds
  );
}

/**
 * Upload Audio sends the current successful-chunk count to merge. It stays
 * disabled until every outstanding chunk upload has settled and at least one
 * succeeded, so the end of the visit is not finalized early.
 */
export function shouldDisableUploadAudio({
  successfulChunkUploads,
  inFlightChunkUploads,
}: {
  successfulChunkUploads: number;
  inFlightChunkUploads: number;
}): boolean {
  const successful = Number.isFinite(successfulChunkUploads) ? successfulChunkUploads : 0;
  const inFlight = Number.isFinite(inFlightChunkUploads) ? inFlightChunkUploads : 1;
  return successful < 1 || inFlight > 0;
}

/**
 * Keep checking after stop while a chunk upload can still succeed. A max wait
 * must not freeze Upload Audio on a chunk that lands later.
 */
export function shouldKeepPollingChunkUploads({
  minDelayElapsed,
  inFlightChunkUploads,
}: {
  minDelayElapsed: boolean;
  inFlightChunkUploads: number;
}): boolean {
  if (!minDelayElapsed) {
    return true;
  }
  if (!Number.isFinite(inFlightChunkUploads)) {
    return true;
  }
  return inFlightChunkUploads > 0;
}
